import * as Dialog from '@radix-ui/react-dialog';
import { Close } from '@sds-system/assets';
import { useEffect, useState } from 'react';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Column } from '../Column';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Row } from '../Row';
import {
  StyledPopupContent,
  StyledPopupOverlay,
  StyledPopupRoot,
  StyledPopupWrapper,
} from './Popup.styled';
import type { PopupProps, PopupStopWatchingTodayProps } from './Popup.types';

export const PopupStopWatchingToday = ({ onChange, checked }: PopupStopWatchingTodayProps) => {
  return (
    <Row gap={8} mt={16}>
      <Checkbox
        checkboxSize='2XS'
        checked={checked}
        label='오늘 그만보기'
        onChange={onChange}
        styledComponents={{
          label: { color: 'system.white' },
        }}
      />
    </Row>
  );
};

export const PopupRoot = <T extends boolean | undefined>({
  styledComponents,
  children,
  defaultOpen,
  open,
  key,
  footer,
  container,
  stopWatchingTodayProps,
  isFullPage = false,
  exitHandler,
}: PopupProps<T>) => {
  const [localDefaultOpen, setLocalDefaultOpen] = useState(defaultOpen || false);
  const [stopWatchingToday, setStopWatchingToday] = useState(
    stopWatchingTodayProps?.checked || false,
  );

  const popupCloseHandler = (_stopWatchingToday?: boolean) => {
    setLocalDefaultOpen(false);
    setStopWatchingToday(false);
    if (exitHandler) exitHandler(_stopWatchingToday ? _stopWatchingToday : stopWatchingToday);
  };

  useEffect(() => {
    () => {
      setLocalDefaultOpen(false);
      setStopWatchingToday(false);
    };
  }, []);

  return (
    <StyledPopupRoot
      defaultOpen={localDefaultOpen}
      onOpenChange={popupCloseHandler}
      open={open || localDefaultOpen}
      {...styledComponents?.root}
    >
      <Dialog.Portal container={container}>
        <StyledPopupOverlay className={`radix-popup-overlay popup-overlay ${key}-popup-overlay`}>
          <StyledPopupContent
            className={`popup-content ${key}-popup-content`}
            isFullPage={isFullPage}
            {...styledComponents?.content}
          >
            <StyledPopupWrapper
              className={`popup-wrapper ${key}-popup-wrapper`}
              isFullPage={isFullPage}
              {...styledComponents?.wrapper}
            >
              {isFullPage ? (
                <IconButton
                  bgColor='system.black'
                  br={999}
                  className={`popup-close-button ${key}-popup-close-button`}
                  onClick={() => popupCloseHandler()}
                  position='absolute'
                  right={16}
                  size='S'
                  top={16}
                  variant='ghost.primary'
                  {...styledComponents?.closeButton}
                >
                  <Icon color='system.white' size='3XS' source={Close} />
                </IconButton>
              ) : (
                <IconButton
                  className={`popup-close-button ${key}-popup-close-button`}
                  onClick={() => popupCloseHandler()}
                  position='absolute'
                  right={0}
                  size='L'
                  variant='ghost.assistive'
                  {...styledComponents?.closeButton}
                >
                  <Icon size='XS' source={Close} />
                </IconButton>
              )}

              {children}
              {isFullPage && (
                <Column
                  alignItems='center'
                  className={`popup-fullPage-button-wrapper ${key}-popup-fullPage-button-wrapper`}
                  css={{ marginTop: 'auto', padding: '0px 16px 16px 16px' }}
                  gap={8}
                >
                  <Button
                    onClick={() => popupCloseHandler()}
                    size='L'
                    styledComponents={{ root: { width: '100%' } }}
                  >
                    닫기
                  </Button>
                  <Button
                    onClick={() => popupCloseHandler(true)}
                    size='M'
                    styledComponents={{ children: { color: 'text.interactive.secondary' } }}
                    variant='ghost.assistive'
                  >
                    오늘 그만보기
                  </Button>
                </Column>
              )}
            </StyledPopupWrapper>
            {!isFullPage && (
              <>
                {footer}
                {stopWatchingTodayProps && (
                  <PopupStopWatchingToday
                    checked={stopWatchingToday}
                    onChange={(e) => {
                      setStopWatchingToday(e.target.checked);
                      if (stopWatchingTodayProps.onChange) stopWatchingTodayProps.onChange(e);
                    }}
                  />
                )}
              </>
            )}
          </StyledPopupContent>
        </StyledPopupOverlay>
      </Dialog.Portal>
    </StyledPopupRoot>
  );
};

export const Popup = Object.assign(PopupRoot, {
  StopWatchingToday: PopupStopWatchingToday,
});
